// src/components/SubscriptionManagement/BillingHistory.jsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Skeleton,
  Alert
} from '@mui/material';
import { Download, Visibility } from '@mui/icons-material';
import { ColorsConstants } from '../../cssUtils/constants.js';
import { GET_INVOICES_LIST, API_BASE_URL } from '../../config/urls.js'; 
import AppTable from '../../common/app-table.jsx';
import { useBranding } from '../../hooks/brandingProvider';
import { formatDateOnly } from '../../utils/dateUtils';
import { getCurrencySymbol } from './utils.js';

const BillingHistory = ({ billingCurrency = "INR" }) => {
  const { favicon_url: faviconUrl } = useBranding() || {};
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoadingId, setActionLoadingId] = useState(null); // Track which row is loading
  const [error, setError] = useState(null);

  // 1. Fetch Invoices
  useEffect(() => {
    const fetchInvoices = async () => {
      try {
        const tenantId = localStorage.getItem('tenant');
        const token = localStorage.getItem('token'); 
        
        const response = await axios.get(GET_INVOICES_LIST, {
          headers: { 
            'X-Tenant-ID': tenantId,
            'Authorization': `Bearer ${token}` 
          }
        });
        
        const data = Array.isArray(response.data) ? response.data : response.data.results || [];
        setInvoices(data);
        
      } catch (err) {
        console.error("❌ Failed to fetch invoices:", err);
        setError("Failed to load billing history.");
      } finally {
        setLoading(false);
      }
    };

    fetchInvoices();
  }, []);

  // 2. Helper to Construct Full URL safely using API_BASE_URL
  const getFullDownloadUrl = (relativeUrl) => {
    if (!relativeUrl) return '';
    if (relativeUrl.startsWith('http')) return relativeUrl;
    
    // Remove leading slash from path if it exists
    const cleanPath = relativeUrl.startsWith('/') ? relativeUrl.substring(1) : relativeUrl;
    
    // Remove trailing slash from base if it exists
    const cleanBase = API_BASE_URL.endsWith('/') ? API_BASE_URL.substring(0, API_BASE_URL.length - 1) : API_BASE_URL;
    
    return `${cleanBase}/${cleanPath}`;
  };

  // 3. Handle PDF Action (Download or Preview)
  const handlePdfAction = async (invoice, mode = 'download') => {
    setActionLoadingId(invoice.id);
    const tenantId = localStorage.getItem("tenant");
    const token = localStorage.getItem('token'); 
    
    try {
        const fullUrl = getFullDownloadUrl(invoice.download_url);
        
        const response = await axios.get(fullUrl, {
            headers: {
                "X-Tenant-ID": tenantId,
                "Authorization": `Bearer ${token}`, 
                "Content-Type": "application/pdf", 
            },
            responseType: 'blob' 
        });

        // Create Blob URL
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const blobUrl = window.URL.createObjectURL(blob);

        if (mode === 'preview') {
            // ✅ Convert PDF blob to base64 data URL
            const reader = new FileReader();
            reader.onloadend = () => {
              const base64PDF = reader.result; // data:application/pdf;base64,...
              const faviconLink = faviconUrl
                ? `<link rel="icon" href="${faviconUrl}" /><link rel="shortcut icon" href="${faviconUrl}" />`
                : '';

              // ✅ Create HTML wrapper with favicon and embedded PDF
              const htmlContent = `
              <!DOCTYPE html>
              <html lang="en">
              <head>
                  <meta charset="utf-8" />
                  ${faviconLink}
                  <meta name="viewport" content="width=device-width, initial-scale=1" />
                  <meta name="theme-color" content="#000000" />
                  <style>
                      * { margin: 0; padding: 0; box-sizing: border-box; }
                      body { background-color: #f5f5f5; font-family: Arial, sans-serif; }
                      .container { width: 100%; height: 100vh; display: flex; align-items: center; justify-content: center; }
                      embed, object { width: 100%; height: 100%; }
                      @media (max-width: 768px) {
                          .container { padding: 10px; }
                      }
                  </style>
              </head>
              <body>
                  <div class="container">
                      <embed src="${base64PDF}" type="application/pdf" />
                  </div>
              </body>
              </html>
              `;
              
              const htmlBlob = new Blob([htmlContent], { type: 'text/html' });
              const htmlUrl = window.URL.createObjectURL(htmlBlob);
              window.open(htmlUrl, "_blank");
            };
            reader.readAsDataURL(blob);
        } else {
            // ✅ Convert PDF blob to base64 data URL for download
            const reader = new FileReader();
            reader.onloadend = () => {
              const base64PDF = reader.result; // data:application/pdf;base64,...
              const faviconPath = `${window.location.origin}/api/whitelabeling/favicons/botbridge.ico`;
              
              // ✅ Fetch favicon and convert to base64 for embedding
              fetch(faviconPath)
                .then(res => res.blob())
                .then(faviconBlob => {
                  const faviconReader = new FileReader();
                  faviconReader.onloadend = () => {
                    const base64Favicon = faviconReader.result;
                    
                    // ✅ Create HTML wrapper with embedded favicon (base64) and PDF
                    const htmlContent = `
                    <!DOCTYPE html>
                    <html lang="en">
                    <head>
                        <meta charset="utf-8" />
                        <link rel="icon" href="${base64Favicon}" />
                        <meta name="viewport" content="width=device-width, initial-scale=1" />
                        <meta name="theme-color" content="#000000" />
                        <style>
                            * { margin: 0; padding: 0; box-sizing: border-box; }
                            body { background-color: #f5f5f5; font-family: Arial, sans-serif; }
                            .container { width: 100%; height: 100vh; display: flex; align-items: center; justify-content: center; }
                            embed, object { width: 100%; height: 100%; }
                            @media (max-width: 768px) {
                                .container { padding: 10px; }
                            }
                        </style>
                    </head>
                    <body>
                        <div class="container">
                            <embed src="${base64PDF}" type="application/pdf" />
                        </div>
                    </body>
                    </html>
                    `;
                    
                    // ✅ Create HTML blob and download it
                    const htmlBlob = new Blob([htmlContent], { type: 'text/html' });
                    const htmlUrl = window.URL.createObjectURL(htmlBlob);
                    const link = document.createElement("a");
                    const safeName = invoice.invoice_number ? invoice.invoice_number.replace(/\//g, '_') : 'invoice';
                    link.href = htmlUrl;
                    link.download = `${safeName}.html`;
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    window.URL.revokeObjectURL(htmlUrl);
                  };
                  faviconReader.readAsDataURL(faviconBlob);
                })
                .catch(err => {
                  console.warn("Failed to fetch favicon, proceeding without embedded favicon:", err);
                  // Fallback: proceed without favicon if fetch fails
                  const htmlContent = `
                  <!DOCTYPE html>
                  <html lang="en">
                  <head>
                      <meta charset="utf-8" />
                      <meta name="viewport" content="width=device-width, initial-scale=1" />
                      <meta name="theme-color" content="#000000" />
                      <style>
                          * { margin: 0; padding: 0; box-sizing: border-box; }
                          body { background-color: #f5f5f5; font-family: Arial, sans-serif; }
                          .container { width: 100%; height: 100vh; display: flex; align-items: center; justify-content: center; }
                          embed, object { width: 100%; height: 100%; }
                          @media (max-width: 768px) {
                              .container { padding: 10px; }
                          }
                      </style>
                  </head>
                  <body>
                      <div class="container">
                          <embed src="${base64PDF}" type="application/pdf" />
                      </div>
                  </body>
                  </html>
                  `;
                  const htmlBlob = new Blob([htmlContent], { type: 'text/html' });
                  const htmlUrl = window.URL.createObjectURL(htmlBlob);
                  const link = document.createElement("a");
                  const safeName = invoice.invoice_number ? invoice.invoice_number.replace(/\//g, '_') : 'invoice';
                  link.href = htmlUrl;
                  link.download = `${safeName}.html`;
                  document.body.appendChild(link);
                  link.click();
                  link.remove();
                  window.URL.revokeObjectURL(htmlUrl);
                });
            };
            reader.readAsDataURL(blob);
        }

        // Cleanup blob URL after a short delay to allow download to start
        setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);

    } catch (error) {
        console.error(`Error ${mode}ing invoice:`, error);
        alert(`Failed to ${mode} invoice.`);
    } finally {
        setActionLoadingId(null);
    }
  };

  const getStatusChip = (status) => {
    const normalized = status ? status.toLowerCase() : 'pending';
    const configs = {
      paid: { label: 'Paid', color: '#c6f6d5', textColor: '#22543d' },
      pending: { label: 'Pending', color: '#fef5e7', textColor: '#975a16' },
      failed: { label: 'Failed', color: '#fed7d7', textColor: '#742a2a' },
    };
    const config = configs[normalized] || configs.pending;

    return (
      <Chip
        label={config.label}
        size="small"
        sx={{ backgroundColor: config.color, color: config.textColor, fontWeight: 600, fontSize: '0.75rem' }}
      />
    );
  };

  const getInvoiceTypeChip = (invoice_type) => {
    const configs = {
      subscription:    { label: 'Subscription', color: '#EDE9FE', textColor: '#5B21B6' },
      addon_recurring: { label: 'Add-on',        color: '#DBEAFE', textColor: '#1E40AF' },
      addon_one_time:  { label: 'One-Time',       color: '#FEF3C7', textColor: '#92400E' },
      addon_mixed:     { label: 'Mixed Add-on',   color: '#F3E8FF', textColor: '#6D28D9' },
    };
    const config = configs[invoice_type] || configs.subscription;
    return (
      <Chip
        label={config.label}
        size="small"
        sx={{ backgroundColor: config.color, color: config.textColor, fontWeight: 600, fontSize: '0.75rem' }}
      />
    );
  };

  const subscriptionColumns = [
    {
      id: "invoice_date",
      label: "Invoice Date",
      render: (invoice) => (
        <span style={{ color: ColorsConstants.primaryGray70 }}>
          {formatDateOnly(invoice.invoice_date)}
        </span>
      ),
    },
    {
      id: "invoice_number",
      label: "Invoice #",
      render: (invoice) => (
        <span style={{ color: ColorsConstants.primaryGray70 }}>
          {invoice.invoice_number}
        </span>
      ),
    },
    {
      id: "total_amount",
      label: "Amount",
      render: (invoice) => (
        <span style={{ color: ColorsConstants.primaryBackgroundColor, fontWeight: 600 }}>
          {getCurrencySymbol(billingCurrency)}{parseFloat(invoice.total_amount).toLocaleString('en-IN')}
        </span>
      ),
    },
    {
      id: "invoice_type",
      label: "Type",
      render: (invoice) => getInvoiceTypeChip(invoice.invoice_type),
    },
    {
      id: "status",
      label: "Status",
      render: (invoice) => getStatusChip(invoice.status),
    },
    {
      id: "__actions__",
      label: "Actions",
      align: "center",
      render: (invoice) => (
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
          <Tooltip title="Preview Invoice">
            <IconButton
              size="small"
              onClick={() => handlePdfAction(invoice, 'preview')}
              disabled={actionLoadingId === invoice.id}
              sx={{ color: ColorsConstants.primaryBlue100 }}
            >
              <Visibility fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Download Invoice">
            <IconButton
              size="small"
              onClick={() => handlePdfAction(invoice, 'download')}
              disabled={actionLoadingId === invoice.id}
              sx={{ color: ColorsConstants.primaryPurple100 }}
            >
              {actionLoadingId === invoice.id
                ? <span style={{ fontSize: '10px' }}>...</span>
                : <Download fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];
 
  // ── Render ─────────────────────────────────────────────────────────────────
 
  if (loading) {
    return (
      <Card sx={{ border: `1px solid ${ColorsConstants.primaryGray10}`, p: 3 }}>
        <Skeleton variant="text" width={200} height={40} sx={{ mb: 3 }} />
        <Skeleton variant="rectangular" height={300} />
      </Card>
    );
  }

  return (
    <Card sx={{ border: `1px solid ${ColorsConstants.primaryGray10}` }}>
      <Box sx={{ p: 3 }}>
        {/* <Typography variant="h5" sx={{ fontWeight: 'bold', color: ColorsConstants.primaryBackgroundColor, mb: 3 }}>
          Billing History
        </Typography> */}

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {invoices.length === 0 ? (
           <Typography sx={{ color: ColorsConstants.primaryGray70, textAlign: 'center', py: 5 }}>
               No invoices found.
           </Typography>
        ) : (
        <AppTable
          columns={subscriptionColumns}
          rows={invoices}
          rowKey={(invoice, index) => index}
          emptyMessage="No invoices found."
          headCellSx={{
            backgroundColor: ColorsConstants.primaryBlue60,
            color: ColorsConstants.primaryGray70,
            fontWeight: 600,
            borderBottom: "none",
          }}
          rowSx={() => ({
            backgroundColor: "#ffffff",
            "&:hover": { backgroundColor: ColorsConstants.primaryBlue60 },
          })}
          paperSx={{ border: "none", boxShadow: "none", mb: 0 }}
          stickyHeader={false}
          hideMainScroll={false}
        />
        )}
      </Box>
    </Card>
  );
};

export default BillingHistory;